// src/analysisHelpers.js

export const INTERVIEW_STATUSES = [
  "Recruiter Contacted",
  "Assessment Sent",
  "Phone Screen",
  "Technical Interview",
  "Final Interview",
  "Offer",
];

export function getISOWeek(date) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
}

/** Weekly trend: apps / interviews / offers per week */
export function buildWeeklyTrend(apps) {
  const map = new Map();

  apps.forEach((job) => {
    if (!job.date_applied) return;
    const d = new Date(job.date_applied);
    if (isNaN(d)) return;

    const year = d.getFullYear();
    const week = getISOWeek(d);
    const key = `${year}-W${week}`;

    const rec = map.get(key) || {
      key,
      year,
      week,
      label: `W${week}`,
      applications: 0,
      interviews: 0,
      offers: 0,
    };

    rec.applications++;
    if (INTERVIEW_STATUSES.includes(job.status)) rec.interviews++;
    if (job.status === "Offer") rec.offers++;

    map.set(key, rec);
  });

  return Array.from(map.values()).sort((a, b) =>
    a.year === b.year ? a.week - b.week : a.year - b.year
  );
}

/** Predict when the next interview is likely based on your real history */
export function buildInterviewPrediction(apps) {
  if (!apps.length) return null;

  const validApps = apps.filter((j) => j.date_applied);
  if (!validApps.length) return null;

  const totalApps = validApps.length;
  const interviews = validApps.filter((j) =>
    INTERVIEW_STATUSES.includes(j.status)
  ).length;

  const interviewRate = interviews > 0 ? interviews / totalApps : 0.1;

  const dates = validApps.map((j) => new Date(j.date_applied));
  const minDate = new Date(Math.min(...dates));
  const daysSpan = Math.max(
    1,
    Math.round((new Date() - minDate) / (1000 * 60 * 60 * 24))
  );
  const appsPerDay = totalApps / daysSpan;

  const expectedAppsForInterview = 1 / interviewRate;
  const expectedDays = expectedAppsForInterview / (appsPerDay || 1);

  const expectedDate = new Date();
  expectedDate.setDate(expectedDate.getDate() + Math.round(expectedDays));

  return {
    interviewRate,
    appsPerDay,
    expectedAppsForInterview,
    expectedDays,
    expectedDateStr: expectedDate.toISOString().slice(0, 10),
  };
}

/** Estimate offer probability per job based on matchScore + status */
export function estimateOfferProbability(job, globalRate = 0.03) {
  // ~3% per application when doing things right
  const base = globalRate * 0.25 || 0.025;

  const matchFactor = job.matchScore
    ? Math.max(0.4, Math.min(1, job.matchScore / 100))
    : 0.7;

  const statusWeight = {
    Saved: 0.2,
    Applied: 1,
    "Recruiter Contacted": 2,
    "Assessment Sent": 2.5,
    "Phone Screen": 3,
    "Technical Interview": 4,
    "Final Interview": 5,
    Offer: 10,
    Rejected: 0,
    Ghosted: 0.1,
  }[job.status] ?? 1;

  let p = base * matchFactor * statusWeight;

  return Math.min(Math.max(p, 0), 0.9);
}
